import { RefObject, useEffect, useRef } from 'react';
import { observeScrollToBottom } from '@/utils/dom';

/** Retry window when a fired trigger never produced a fetch. */
export const RETRY_UNLOCK_MS = 1500;

export type ScrollContainer = string | RefObject<HTMLElement | null>;

export type UseInfiniteScrollPaginationOptions = {
  /** Scrolling element: a CSS selector (e.g. `#my-table .ant-table-body`) or a ref. Undefined disables paging. */
  container: ScrollContainer | undefined;
  /** False detaches the scroll observer entirely (e.g. grouped views). */
  enabled?: boolean;
  /** True while a fetch is in flight; its falling edge re-arms the trigger. */
  loading?: boolean;
  /** There is a next page and the loaded state matches the current filters. */
  canLoadMore: boolean;
  /** Latest response's pagination links; a new identity re-arms the trigger. */
  links?: unknown;
  /** Extra synchronous gate evaluated at trigger time. */
  shouldLoad?: () => boolean;
  /** Distance in px from the bottom that counts as "reached the end". */
  offset?: number;
  /** Starts the next-page fetch. Must actually fetch — gating belongs in the options above. */
  onLoadMore: () => void;
};

/**
 * Canonical infinite-scroll pagination: observes the container, fires exactly once
 * per response, and tops up when a page is too short to make the container scrollable
 * (an unscrollable container never emits scroll events). See docs/tables.md.
 */
export function useInfiniteScrollPagination({
  container,
  enabled = true,
  loading = false,
  canLoadMore,
  links,
  shouldLoad,
  offset = 100,
  onLoadMore,
}: UseInfiniteScrollPaginationOptions) {
  // Refs because scroll events outrun React renders: the lock must flip synchronously,
  // and the observer binds once per container while reading the latest render's values.
  const lockedRef = useRef(false);
  const unlockTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const latestRef = useRef({ enabled, loading, canLoadMore, shouldLoad, onLoadMore });
  latestRef.current = { enabled, loading, canLoadMore, shouldLoad, onLoadMore };

  const lock = () => {
    lockedRef.current = true;
    clearTimeout(unlockTimerRef.current);
    unlockTimerRef.current = setTimeout(() => {
      lockedRef.current = false;
    }, RETRY_UNLOCK_MS);
  };

  const unlock = () => {
    clearTimeout(unlockTimerRef.current);
    lockedRef.current = false;
  };

  const fire = () => {
    const latest = latestRef.current;
    if (!latest.enabled || latest.loading || !latest.canLoadMore) return;
    if (lockedRef.current) return;
    if (latest.shouldLoad && !latest.shouldLoad()) return;
    lock();
    latest.onLoadMore();
  };

  const resolveContainer = (): HTMLElement | null => {
    if (!container) return null;
    return typeof container === 'string'
      ? document.querySelector<HTMLElement>(container)
      : container.current;
  };

  useEffect(() => {
    if (!loading) unlock();
  }, [loading, links]);

  // Rebinds after each fetch so containers that mount late (drawer bodies,
  // tables that render their scroller with the first data) still get observed.
  useEffect(() => {
    if (!enabled) return;
    const scroller = resolveContainer();
    if (!scroller) return;
    return observeScrollToBottom({ container: scroller, offset, callback: fire });
  }, [enabled, container, offset, loading, links]);

  // Top-up; clientHeight 0 means the container is hidden, so paging it is waste.
  useEffect(() => {
    if (!enabled) return;
    const scroller = resolveContainer();
    if (!scroller) return;
    if (scroller.clientHeight > 0 && scroller.scrollHeight <= scroller.clientHeight) fire();
  }, [enabled, container, loading, canLoadMore, links]);

  useEffect(() => () => clearTimeout(unlockTimerRef.current), []);
}
